import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, Table, Button, Spinner, Form, Row, Col, Badge } from "react-bootstrap";
import { Navigate } from "react-router-dom";
import Swal from "sweetalert2";
import {API_BASE} from "../../services/apirest";
// =============================================
// Ajusta esto a tu backend
// =============================================

const MOVS_ENDPOINT = `${API_BASE}/solicitudes.php`; // ?movimientos=1

// Enum de acciones (para filtro servidor)
const ACCIONES = [
  "CREAR","CAMBIO_ESTADO","CAMBIO_STATUS",
  "BAJA_SOLICITADA","BAJA_APROBADA","BAJA_EJECUTADA","BAJA_VISTO",
  "REACT_SOLICITADA","REACT_EJECUTADA","REACT_VISTO",
  "MOD_SOLICITADA","MOD_APROBADA","MOD_EJECUTADA","MOD_RECHAZADA",
  "EDITAR",
];

// =============== Utils ===============
const buildQuery = (params) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== "") qs.append(k, v);
  });
  return qs.toString();
};

function parseCambios(c) {
  if (!c) return {};
  if (typeof c === "object") return c;
  try { return JSON.parse(c); } catch { return { raw: String(c) }; }
}

// Id normalizado (acepta varias keys)
function normalizeSolicitudId(row) {
  const raw =
    row?.SolicitudId ??
    row?.SolicitudID ??
    row?.solicitud_id ??
    row?.solicitudId ??
    row?.solicitudID ??
    null;
  if (raw == null) return null;
  return String(raw).trim();
}

// ======= ESTADO → DISPLAY (agrupación para buckets) =======
const ESTADO_DISPLAY_MAP = {
  Pendiente:   "Pendiente de validación",
  "En proceso":"Pendiente revisión",
  Aprobado:    "Pendiente revisión",
  Completado:  "Pendiente de recepción",
  Visto:       "En plantilla",
};

function deriveRegistrandoDisplayFromCambios(row) {
  const ch = parseCambios(row?.cambios_json);
  const hasFGU =
    !!ch?.fgu_usuario || !!ch?.fgu_usuario_red ||
    !!ch?.a_fgu_usuario || !!ch?.a_fgu_usuario_red;

  const hasCreds =
    !!ch?.usuario_red || !!ch?.cvven ||
    !!ch?.a_usuario_red || !!ch?.a_cvven;

  if (hasCreds && !hasFGU) return "Generar Credenciales";
  if (hasFGU && !hasCreds) return "Creación FGU";
  if (hasFGU && hasCreds)  return "Generar Credenciales";
  return "Creación FGU";
}

function getEstadoDisplayFromRow(row, which = "a") {
  const key = which === "de" ? "de_estado" : "a_estado";
  const raw = String(row?.[key] || "").trim();
  if (!raw) return "-";
  if (raw === "Registrando") return deriveRegistrandoDisplayFromCambios(row);
  const normalized = raw.replace(/\s+$/, "");
  return ESTADO_DISPLAY_MAP[normalized] || normalized;
}

// ========================
// Cálculo de tiempos
// ========================

const BUCKET_TO_FIELD = {
  "Pendiente de validación": "pendiente_validacion",
  "Pendiente revisión": "pendiente_revision",
  "Creación FGU": "creacion_fgu",
  "Generar Credenciales": "generar_credenciales",
  "Pendiente de recepción": "pendiente_recepcion",
  "En plantilla": "en_plantilla",
};

function msToHMS(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = String(Math.floor(s / 3600)).padStart(2,'0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2,'0');
  const ss = String(s % 60).padStart(2,'0');
  return `${hh}:${mm}:${ss}`;
}
function hmsToMs(str) {
  if (!str) return 0;
  const [hh="0", mm="0", ss="0"] = String(str).split(":");
  return ((+hh)*3600 + (+mm)*60 + (+ss)) * 1000;
}

function bucketForMovement(row) {
  return getEstadoDisplayFromRow(row, "a");
}

function parseTs(val) {
  if (!val) return null;
  const t = new Date(String(val).replace(' ', 'T')).getTime();
  return Number.isFinite(t) ? t : null;
}

// ===== Tipo/Acción por movimiento (evita falsos "Modificación")
function getMovimientoTipo(row) {
  const act = String(row?.accion || "").toUpperCase();

  const isBaja = act.startsWith("BAJA_") || row?.a_baja_stage || row?.de_baja_stage;
  const isReac = act.startsWith("REACT_") || row?.a_reactivacion || row?.de_reactivacion;

  let isMod =
    act.startsWith("MOD_") ||
    row?.a_mod_stage || row?.de_mod_stage ||
    row?.a_modificaciones_stage || row?.de_modificaciones_stage;

  if (!isMod) {
    const ch = parseCambios(row?.cambios_json);
    if (ch && ch.de_rol !== undefined && ch.a_rol !== undefined) {
      if (String(ch.de_rol) !== String(ch.a_rol)) isMod = true;
    }
  }

  if (isBaja) return "Baja";
  if (isReac) return "Reactivación";
  if (isMod)  return "Modificación";
  return "Registro";
}

function getAccionDisplay(row) {
  const act = String(row?.accion || "").toUpperCase();
  const map = {
    BAJA_SOLICITADA: "Baja · Solicitada",
    BAJA_APROBADA:   "Baja · Aprobada",
    BAJA_EJECUTADA:  "Baja · Ejecutada",
    BAJA_VISTO:      "Baja · Visto",

    REACT_SOLICITADA:"Reactivación · Solicitada",
    REACT_EJECUTADA: "Reactivación · Ejecutada",
    REACT_VISTO:     "Reactivación · Visto",

    MOD_SOLICITADA:  "Modificación · Solicitada",
    MOD_APROBADA:    "Modificación · Aprobada",
    MOD_EJECUTADA:   "Modificación · Ejecutada",
    MOD_RECHAZADA:   "Modificación · Rechazada",

    CREAR:           "Registro",
    EDITAR:          "Edición",
  };

  if (map[act]) return map[act];
  if (act === "CAMBIO_ESTADO")  return `Estado: ${row?.de_estado ?? "-"} → ${row?.a_estado ?? "-"}`;
  if (act === "CAMBIO_STATUS")  return `Status: ${row?.de_status ?? "-"} → ${row?.a_status ?? "-"}`;
  return getMovimientoTipo(row);
}

// Construye la “línea de tiempo” por movimiento
function buildTimeline(rows, includeOpenTail = false) {
  const bySol = new Map();

  for (const r of rows) {
    const sid = normalizeSolicitudId(r);
    if (!sid) continue;
    if (!bySol.has(sid)) bySol.set(sid, []);
    bySol.get(sid).push(r);
  }

  const out = [];

  for (const [sid, list] of bySol) {
    list.sort((a,b) => parseTs(a.creado_en) - parseTs(b.creado_en));

    for (let i = 0; i < list.length; i++) {
      const cur  = list[i];
      const next = list[i+1];

      const start = parseTs(cur?.creado_en);
      const end   = next ? parseTs(next?.creado_en) : (includeOpenTail ? Date.now() : null);
      if (!start || !end) continue;

      const bucket = bucketForMovement(cur);
      const durMs  = Math.max(0, end - start);
      const durStr = msToHMS(durMs);

      const rowDur = {
        pendiente_validacion: "00:00:00",
        pendiente_revision: "00:00:00",
        creacion_fgu: "00:00:00",
        generar_credenciales: "00:00:00",
        pendiente_recepcion: "00:00:00",
      };
      const field = BUCKET_TO_FIELD[bucket];
      if (field) rowDur[field] = durStr;

      out.push({
        SolicitudId: sid,
        tipo: getMovimientoTipo(cur),
        accion: getAccionDisplay(cur),
        inicio: new Date(start).toISOString(),
        fin:    new Date(end).toISOString(),
        total:  durStr,
        ...rowDur,
      });
    }
  }

  out.sort((a,b) => (parseTs(b.inicio) || 0) - (parseTs(a.inicio) || 0));
  return out;
}

// === Colapsar: uno solo por paso (SolicitudId + Tipo) ===
const DURATION_FIELDS = [
  "pendiente_validacion",
  "pendiente_revision",
  "creacion_fgu",
  "generar_credenciales",
  "pendiente_recepcion",
  "total",
];

function collapseByTipo(rows) {
  // key: `${SolicitudId}::${tipo}`
  const acc = new Map();

  for (const r of rows) {
    const key = `${r.SolicitudId}::${r.tipo}`;
    const cur = acc.get(key) || {
      SolicitudId: r.SolicitudId,
      tipo: r.tipo,
      accion: "—",
      inicio_ms: parseTs(r.inicio) || Infinity, // guardo ms para min/max
      fin_ms:    parseTs(r.fin)    || 0,
      sums: {
        pendiente_validacion: 0,
        pendiente_revision: 0,
        creacion_fgu: 0,
        generar_credenciales: 0,
        pendiente_recepcion: 0,
        total: 0,
      }
    };

    cur.inicio_ms = Math.min(cur.inicio_ms, parseTs(r.inicio) || cur.inicio_ms);
    cur.fin_ms    = Math.max(cur.fin_ms,    parseTs(r.fin)    || cur.fin_ms);

    for (const f of DURATION_FIELDS) cur.sums[f] += hmsToMs(r[f]);

    acc.set(key, cur);
  }

  // materializa filas
  const out = [];
  for (const [,v] of acc) {
    out.push({
      SolicitudId: v.SolicitudId,
      tipo: v.tipo,
      accion: "—",
      inicio: Number.isFinite(v.inicio_ms) ? new Date(v.inicio_ms).toISOString() : null,
      fin:    v.fin_ms ? new Date(v.fin_ms).toISOString() : null,
      pendiente_validacion: msToHMS(v.sums.pendiente_validacion),
      pendiente_revision:   msToHMS(v.sums.pendiente_revision),
      creacion_fgu:         msToHMS(v.sums.creacion_fgu),
      generar_credenciales: msToHMS(v.sums.generar_credenciales),
      pendiente_recepcion:  msToHMS(v.sums.pendiente_recepcion),
      total:                msToHMS(v.sums.total),
    });
  }

  // ordena por inicio desc (como la vista original)
  out.sort((a,b) => (parseTs(b.inicio) || 0) - (parseTs(a.inicio) || 0));
  return out;
}

// ===== CSV (exporta lo que se ve) =====
function toRowsCSV(rows) {
  const headers = [
    "SolicitudId","tipo","accion",
    "inicio","fin","total",
    "pendiente_validacion","pendiente_revision",
    "creacion_fgu","generar_credenciales",
    "pendiente_recepcion"
  ];

  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return (s.includes(",") || s.includes("\n") || s.includes("\""))
      ? `"${s.replaceAll('"','""')}"`
      : s;
  };

  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push([
      r.SolicitudId, r.tipo, r.accion,
      r.inicio, r.fin, r.total,
      r.pendiente_validacion, r.pendiente_revision,
      r.creacion_fgu, r.generar_credenciales,
      r.pendiente_recepcion
    ].map(esc).join(","));
  }
  return lines.join("\n");
}

// BOM para acentos correctos en Excel
function downloadText(filename, text) {
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + text], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// =============== Fallback compat ===============
const mapSolicitudToMovimiento = (s) => ({
  movimiento_id: null,
  creado_en: s.Fecha || s.creado_en || null,
  SolicitudId: normalizeSolicitudId(s) || s.SolicitudId || s.SolicitudID || null,
  accion: "CREAR",
  de_estado: null,
  a_estado: s.Estado ?? null,
  de_status: null,
  a_status: s.status ?? null,
  de_baja_stage: null,
  a_baja_stage: s.baja_stage ?? null,
  de_reactivacion: null,
  a_reactivacion: s.reactivacion_stage ?? null,
  de_mod_stage: null,
  a_mod_stage: s.modificaciones_stage ?? null,
  nota: "APP (compat)",
  cambios_json: null,
});

export default function MovimientosSolicitudes() {
  const rolId = parseInt(localStorage.getItem("rol_id"), 10);
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  // Filtros (SERVIDOR)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [accion, setAccion] = useState("");
  const [origen, setOrigen] = useState(""); // TRIGGER | APP | (vacío = todos)

  // Filtro (CLIENTE) — por SolicitudId
  const [solicitudId, setSolicitudId] = useState("");

  // Datos crudos de movimientos
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pageSize, setPageSize] = useState(500);
  const [fallbackInfo, setFallbackInfo] = useState(null);

  // Contar último tramo hasta ahora
  const [includeOpenTail, setIncludeOpenTail] = useState(false);
  // Agrupar por paso (uno por tipo)
  const [groupByTipo, setGroupByTipo] = useState(true);

  const sortByDateDesc = (arr, get) =>
    [...arr].sort((a, b) => {
      const ta = new Date(String(get(a) || "").replace(" ", "T")).getTime() || 0;
      const tb = new Date(String(get(b) || "").replace(" ", "T")).getTime() || 0;
      return tb - ta;
    });

  const doFetch = async () => {
    try {
      setLoading(true);
      setFallbackInfo(null);

      const params = {
        movimientos: 1,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        accion: accion || undefined,
        origen: origen || undefined,
        limit: pageSize,
        offset: 0,
      };
      const qs = buildQuery(params);

      // Movimientos reales
      const res = await fetch(`${MOVS_ENDPOINT}?${qs}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      let list = json?.result?.movimientos;

      // Ordenar por fecha desc
      if (Array.isArray(list) && list.length > 0) {
        list = sortByDateDesc(list, r => r.creado_en);
      }

      // Fallback si no hay tabla de movimientos
      if (!Array.isArray(list) || list.length === 0) {
        let sols = json?.result?.solicitudes;
        if (!Array.isArray(sols) || sols.length === 0) {
          const params2 = { limit: pageSize, offset: 0 };
          const qs2 = buildQuery(params2);
          const res2 = await fetch(`${MOVS_ENDPOINT}?${qs2}`, { headers });
          if (res2.ok) {
            const json2 = await res2.json();
            sols = json2?.result?.solicitudes || [];
          }
        }
        if (Array.isArray(sols) && sols.length > 0) {
          const solsSorted = sortByDateDesc(sols, s => s.Fecha || s.creado_en);
          const take = solsSorted.slice(0, pageSize);
          list = take.map(mapSolicitudToMovimiento);
          setFallbackInfo(`Mostrando últimos ${list.length} desde "solicitudes" (sin registros en movimientos).`);
        } else {
          list = [];
        }
      }

      setRows(list);
    } catch (err) {
      console.error(err);
      setRows([]);
      setFallbackInfo(null);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudieron cargar movimientos." });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { doFetch(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { doFetch(); }, [dateFrom, dateTo, accion, origen, pageSize]);


  // Filtrado mínimo por SolicitudId antes de construir la línea de tiempo
  const movsFiltrados = useMemo(() => {
    if (!solicitudId) return rows;
    const needle = String(solicitudId).trim();
    return rows.filter(r => (normalizeSolicitudId(r) || "").includes(needle));
  }, [rows, solicitudId]);

  // Línea de tiempo por movimiento
  const timeline = useMemo(
    () => buildTimeline(movsFiltrados, includeOpenTail),
    [movsFiltrados, includeOpenTail]
  );

  // Vista a mostrar (agrupada o por movimiento)
  const displayRows = useMemo(
    () => (groupByTipo ? collapseByTipo(timeline) : timeline),
    [timeline, groupByTipo]
  );

  if (![1, 2, 3, 4, 14].includes(rolId)) return <Navigate to='/' replace />;

  return (
    <div className="app-container">
      <Container className="my-5">
        <Card className="shadow-sm">
          <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="mb-0">Tiempos por solicitud</h5>
            <div className="d-flex align-items-center gap-2">
              <Button
                size="sm"
                variant="success"
                onClick={() => downloadText(`tiempos_${new Date().toISOString().slice(0,10)}.csv`, toRowsCSV(displayRows))}
                title="Exportar tiempos"
              >
                Exportar Tiempos (CSV)
              </Button>
            </div>
          </Card.Header>

          <Card.Body>
            <Form className="mb-3">
              <Row className="g-2">
                <Col md={3} sm={6}>
                  <Form.Label className="mb-1">Desde</Form.Label>
                  <Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </Col>
                <Col md={3} sm={6}>
                  <Form.Label className="mb-1">Hasta</Form.Label>
                  <Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </Col>
                <Col md={3} sm={6}>
                  <Form.Label className="mb-1">Acción</Form.Label>
                  <Form.Select value={accion} onChange={(e) => setAccion(e.target.value)}>
                    <option value="">Todas</option>
                    {ACCIONES.map(a => <option key={a} value={a}>{a}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3} sm={6}>
                  <Form.Label className="mb-1">Origen</Form.Label>
                  <Form.Select value={origen} onChange={(e) => setOrigen(e.target.value)}>
                    <option value="">Todos</option>
                    <option value="TRIGGER">TRIGGER</option>
                    <option value="APP">APP</option>
                  </Form.Select>
                </Col>
              </Row>

              <Row className="g-2 mt-1">
                <Col md={4} sm={12}>
                  <Form.Label className="mb-1">SolicitudId</Form.Label>
                  <Form.Control
                    placeholder="Ej. 110627"
                    value={solicitudId}
                    onChange={(e) => setSolicitudId(e.target.value)}
                  />
                </Col>
                <Col md={4} sm={12} className="d-flex align-items-end gap-3">

                  <Form.Check
                    type="switch"
                    id="toggle-group-tipo"
                    label="Agrupar por paso (uno por tipo)"
                    checked={groupByTipo}
                    onChange={(e) => setGroupByTipo(e.target.checked)}
                  />
                </Col>
                <Col md={4} sm={12} className="d-flex align-items-end">
                  <div className="ms-auto d-flex gap-2">
                    <Form.Select size="sm" style={{ width: 130 }} value={pageSize} onChange={(e)=> setPageSize(parseInt(e.target.value,10))}>
                      {[200,500,1000].map(n => <option key={n} value={n}>{n}/consulta</option>)}
                    </Form.Select>
                    <Button size="sm" variant="primary" onClick={doFetch}>Buscar</Button>
                    {loading && <Spinner animation="border" size="sm" />}
                  </div>
                </Col>
              </Row>
            </Form>

            {fallbackInfo && (
              <div className="alert alert-info py-2 small mb-3">
                {fallbackInfo}
              </div>
            )}

            {/* Tabla */}
            {displayRows.length === 0 ? (
              <p className='text-center text-muted'>No hay datos para los filtros actuales.</p>
            ) : (
              <div className="table-responsive">
                <Table hover size="sm">
                  <thead>
                    <tr>
                      <th>SolicitudID</th>
                      <th>Tipo</th>
                      <th>Acción</th>
                      <th>Inicio</th>
                      <th>Fin</th>
                      <th>Pend. validación</th>
                      <th>Pend. revisión</th>
                      <th>Creación FGU</th>
                      <th>Generar Credenciales</th>
                      <th>Pend. recepción</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((r, idx) => (
                      <tr key={`${r.SolicitudId}-${idx}`}>
                        <td>{r.SolicitudId}</td>
                        <td>{r.tipo}</td>
                        <td>{r.accion}</td>
                        <td>{r.inicio ? new Date(r.inicio).toLocaleString("es-MX") : "-"}</td>
                        <td>{r.fin ? new Date(r.fin).toLocaleString("es-MX") : "-"}</td>
                        <td>{r.pendiente_validacion}</td>
                        <td>{r.pendiente_revision}</td>
                        <td>{r.creacion_fgu}</td>
                        <td>{r.generar_credenciales}</td>
                        <td>{r.pendiente_recepcion}</td>
                        <td><Badge bg="dark">{r.total}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}
